import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';
import { PortalLayout } from '../components/PortalLayout';
import { PortalSidebar } from '../components/PortalSidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { allSections, actSidebarGroups } from '../data/actContent';

const SectionDetail = () => {
  const { id } = useParams();
  const index = allSections.findIndex((s) => String(s.id) === id);
  const section = allSections[index];

  if (!section) return <Navigate to="/dpdpa-act" replace />;

  const prev = allSections[index - 1];
  const next = allSections[index + 1];

  const sectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Legislation',
    name: `Section ${section.id} — ${section.title}`,
    legislationIdentifier: `DPDPA-2023-S${section.id}`,
    description: section.summary,
    isPartOf: 'Digital Personal Data Protection Act, 2023',
  };

  return (
    <>
      <SEO
        title={`Section ${section.id} DPDPA — ${section.title}`}
        description={section.summary}
        path={`/dpdpa-act/section/${section.id}`}
        jsonLd={sectionJsonLd}
      />
      <PortalLayout
        breadcrumb={<Breadcrumbs trail={[{ label: 'DPDPA Act', href: '/dpdpa-act' }, { label: section.chapterLabel, href: `/dpdpa-act?chapter=${section.chapterNumber}` }, { label: `§${section.id}` }]} />}
        sidebar={<PortalSidebar heading="DPDPA Act, 2023" headingHref="/dpdpa-act" groups={actSidebarGroups} activeItemId={section.id} defaultOpenGroupId={section.chapterNumber} />}
      >
        <div data-testid="section-detail-page" className="max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-saffron-600 mb-3">{section.chapterLabel} · {section.range}</p>
          <span className="font-serif text-xl text-gray-400">§{section.id}</span>
          <h1 className="font-serif text-3xl sm:text-4xl text-navy leading-tight mt-1 mb-6">{section.title}</h1>
          <p className="text-gray-700 text-base sm:text-lg leading-[1.75]" data-testid="section-detail-body">{section.summary}</p>

          <div className="flex items-center justify-between gap-4 mt-14 pt-6 border-t border-gray-200" data-testid="section-detail-nav">
            {prev ? (
              <Link to={`/dpdpa-act/section/${prev.id}`} data-testid="section-detail-prev-link" className="flex items-center gap-2 text-gray-500 hover:text-saffron-600 text-sm transition-colors">
                <ArrowLeft size={14} />
                <span>§{prev.id} {prev.title}</span>
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/dpdpa-act/section/${next.id}`} data-testid="section-detail-next-link" className="flex items-center gap-2 text-gray-500 hover:text-saffron-600 text-sm text-right transition-colors">
                <span>§{next.id} {next.title}</span>
                <ArrowRight size={14} />
              </Link>
            ) : <span />}
          </div>
        </div>
      </PortalLayout>
    </>
  );
};

export default SectionDetail;
